import React from 'react';
import ImageAdjustOverlay from '../ImageAdjustOverlay';
import type { ImageAdjustInteractionKind, ImageTransformHandleKind } from './imageBlockEditing';
import ImageTransformHandles from './ImageTransformHandles';

type ImageTransformLayerProps = {
  dragging?: boolean;
  interactionKind?: ImageAdjustInteractionKind | null;
  onExit?: () => void;
  onSurfacePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onSurfacePointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  onSurfacePointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  onSurfaceWheel: (event: WheelEvent) => void;
  onHandlePointerDown: (
    event: React.PointerEvent<HTMLButtonElement>,
    handleKind: ImageTransformHandleKind,
    bounds: DOMRect | null
  ) => void;
  onHandlePointerMove: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onHandlePointerUp: (event: React.PointerEvent<HTMLButtonElement>) => void;
};

const ImageTransformLayer: React.FC<ImageTransformLayerProps> = ({
  dragging = false,
  interactionKind = null,
  onExit,
  onSurfacePointerDown,
  onSurfacePointerMove,
  onSurfacePointerUp,
  onSurfaceWheel,
  onHandlePointerDown,
  onHandlePointerMove,
  onHandlePointerUp,
}) => {
  const isDragging = dragging || interactionKind === 'move';
  const surfaceRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const node = surfaceRef.current;
    if (!node) return;

    const handleWheel = (event: WheelEvent) => {
      onSurfaceWheel(event);
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      node.removeEventListener('wheel', handleWheel);
    };
  }, [onSurfaceWheel]);

  return (
    <div className="absolute inset-0 z-[31]">
      <div
        ref={surfaceRef}
        className={`absolute inset-0 touch-none select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{ overscrollBehavior: 'contain' }}
        data-builder-allow-interaction
        onPointerDown={onSurfacePointerDown}
        onPointerMove={onSurfacePointerMove}
        onPointerUp={onSurfacePointerUp}
        onPointerCancel={onSurfacePointerUp}
      >
        <ImageAdjustOverlay
          dragging={dragging}
          instructionText="Drag image to reposition | Drag handles to scale/stretch | Drag outer handles to rotate | Scroll to zoom"
          onExit={onExit}
        />
      </div>
      <ImageTransformHandles
        interactionKind={interactionKind}
        onHandlePointerDown={onHandlePointerDown}
        onHandlePointerMove={onHandlePointerMove}
        onHandlePointerUp={onHandlePointerUp}
      />
    </div>
  );
};

export default ImageTransformLayer;
